import type { Ctx } from "../app";
import { must, sb } from "../lib/supabase";
import { Badge, Card, ErrorBox, Loading, fmtDate, useLoad } from "../lib/ui";
import { APP_STATUSES } from "../lib/types";

/** Manager view: pipeline, real turnaround per payer, team readiness, risk and data freshness. */
export function InsightsPage({ ctx }: { ctx: Ctx }) {
  const { data, error } = useLoad(async () => {
    const [apps, turn, ready, fresh, tasks] = await Promise.all([
      sb().from("applications").select("status, submitted_on, approved_on, effective_on").eq("org_id", ctx.orgId),
      sb().from("v_payer_turnaround").select("*").eq("org_id", ctx.orgId),
      sb().from("v_provider_readiness").select("*").eq("org_id", ctx.orgId),
      sb().rpc("data_freshness"),
      sb().from("tasks").select("kind, due_on, done_at").eq("org_id", ctx.orgId).is("done_at", null),
    ]);
    return { apps: must(apps) as any[], turn: must(turn) as any[], ready: must(ready) as any[], fresh: (fresh.data ?? []) as any[], tasks: must(tasks) as any[] };
  }, [ctx.orgId]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;

  const today = new Date().toISOString().slice(0, 10);
  const counts = Object.fromEntries(APP_STATUSES.map((s) => [s, data.apps.filter((a) => a.status === s).length]));
  const maxCount = Math.max(1, ...Object.values(counts));
  const approvedLast90 = data.apps.filter((a) => a.approved_on && a.approved_on >= new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10)).length;
  const durations = data.apps.filter((a) => a.approved_on && a.submitted_on)
    .map((a) => (new Date(a.approved_on).getTime() - new Date(a.submitted_on).getTime()) / 86400000).sort((x, y) => x - y);
  const median = durations.length ? Math.round(durations[Math.floor(durations.length / 2)]!) : null;
  const team = data.ready.filter((r) => r.status !== "inactive");
  const withPct = team.filter((r) => r.readiness_pct !== null);
  const avgReady = withPct.length ? Math.round(withPct.reduce((n, r) => n + Number(r.readiness_pct), 0) / withPct.length) : null;
  const atRisk = team.filter((r) => r.failing_checks > 0 || r.expiring_30d > 0);
  const overdue = data.tasks.filter((t) => t.due_on < today).length;
  const neverChecked = team.filter((r) => !r.last_checked_at).length;
  const staleChecks = team.filter((r) => r.last_checked_at && Date.now() - new Date(r.last_checked_at).getTime() > 31 * 86400000).length;

  return (
    <>
      <div class="kpi-grid">
        <Kpi label="Median days submit → approval" value={median ?? "—"} hint={durations.length ? `from ${durations.length} approvals` : "no approvals yet"} />
        <Kpi label="Approvals, last 90 days" value={approvedLast90} />
        <Kpi label="Average BCBA readiness" value={avgReady === null ? "—" : `${avgReady}%`} hint="required checklist items met" />
        <Kpi label="BCBAs at risk" value={atRisk.length} tone={atRisk.length ? "bad" : undefined} hint="failing check or expiring ≤30 days" />
        <Kpi label="Overdue tasks" value={overdue} tone={overdue ? "bad" : undefined} />
        <Kpi label="Verification coverage" value={`${team.length - neverChecked - staleChecks}/${team.length}`} hint="checked in the last 31 days" />
      </div>

      <Card title="Pipeline">
        <div class="bars">{APP_STATUSES.filter((s) => counts[s]).map((s) => (
          <div class="bar-row"><span>{s.replaceAll("_", " ")}</span><div class="bar" style={{ width: `${(100 * counts[s]!) / maxCount}%` }} /><strong>{counts[s]}</strong></div>
        ))}{data.apps.length === 0 && <p class="muted">No applications yet.</p>}</div>
      </Card>

      <Card title="Payer turnaround — measured from your own applications">
        <p class="muted small">This is your real experience, which may differ from what payers publish. It gets more reliable as approvals accumulate.</p>
        {data.turn.length === 0 ? <p class="muted">No applications yet.</p> : (
          <table>
            <thead><tr><th>Payer</th><th>Approved</th><th>Median days</th><th>Avg days</th><th>In flight</th><th>Oldest in flight</th><th>Denied</th><th>Backdates granted</th></tr></thead>
            <tbody>{[...data.turn].sort((a, b) => (b.oldest_in_flight_days ?? 0) - (a.oldest_in_flight_days ?? 0)).map((t) => (
              <tr><td>{t.payer_name}</td><td>{t.approved_count}</td><td>{t.median_days_to_approval ?? "—"}</td><td>{t.avg_days_to_approval ?? "—"}</td>
                <td>{t.in_flight}</td><td>{t.oldest_in_flight_days != null ? <span class={`badge ${t.oldest_in_flight_days > 90 ? "bad" : t.oldest_in_flight_days > 45 ? "warn" : ""}`}>{t.oldest_in_flight_days}d</span> : "—"}</td>
                <td>{t.denied_count}</td><td>{t.backdate_requested ? `${t.backdate_granted}/${t.backdate_requested}` : "—"}</td></tr>))}</tbody>
          </table>
        )}
      </Card>

      <Card title="Team readiness">
        <table>
          <thead><tr><th>BCBA</th><th>Readiness</th><th>To verify</th><th>Expiring ≤30d</th><th>Failing checks</th><th>Billable payers</th><th>Last checked</th></tr></thead>
          <tbody>{[...team].sort((a, b) => (a.readiness_pct ?? 101) - (b.readiness_pct ?? 101)).map((r) => (
            <tr>
              <td><a href={`#/providers/${r.provider_id}`}>{r.last_name}, {r.first_name}</a></td>
              <td><Readiness pct={r.readiness_pct} /></td>
              <td>{r.unverified || "—"}</td>
              <td>{r.expiring_30d ? <span class="badge warn">{r.expiring_30d}</span> : "—"}</td>
              <td>{r.failing_checks ? <span class="badge bad">{r.failing_checks}</span> : "—"}</td>
              <td>{r.effective_payers}/{r.applications}</td>
              <td class="small">{r.last_checked_at ? fmtDate(r.last_checked_at) : <Badge value="never" />}</td>
            </tr>))}</tbody>
        </table>
      </Card>

      <Card title="Data freshness — where the automatic checks get their information">
        <table>
          <thead><tr><th>Source</th><th>Records</th><th>Last loaded</th></tr></thead>
          <tbody>
            {data.fresh.map((f) => <tr><td>{FRESH_LABEL[f.dataset] ?? f.dataset}</td><td>{Number(f.row_count).toLocaleString()}</td><td>{fmtDate(f.loaded_at)}</td></tr>)}
            <tr><td>NPPES, NY Medicaid enrollment, NYSED</td><td>live</td><td>queried at each check</td></tr>
          </tbody>
        </table>
        <p class="muted small">BCBAs are re-checked automatically about every 30 days; a check that changes from pass to fail creates a task and emails the owners/admins.</p>
      </Card>
    </>
  );
}

const FRESH_LABEL: Record<string, string> = {
  oig_leie: "OIG federal exclusion list", ny_omig: "NY OMIG Medicaid exclusion list",
  nj_aba_roster: "NJ Applied Behavior Analyst roster", payers: "Payer requirements (research)",
};

function Kpi({ label, value, hint, tone }: { label: string; value: string | number; hint?: string; tone?: string }) {
  return <div class="stat"><div class={`stat-value ${tone ?? ""}`}>{value}</div><div class="small">{label}</div>{hint && <div class="muted small">{hint}</div>}</div>;
}

export function Readiness({ pct }: { pct: number | null }) {
  if (pct === null || pct === undefined) return <span class="muted small">no payers yet</span>;
  const tone = pct >= 100 ? "" : pct >= 60 ? "warn" : "bad";
  return <div class="row" style={{ margin: 0 }}><div class={`meter ${tone}`}><div style={{ width: `${Math.min(100, pct)}%` }} /></div><span class="small">{pct}%</span></div>;
}
